<template>
  <div class="paused-game-lobby">
    <!-- Header -->
    <header class="pgl-header">
      <button class="pgl-back-btn" @click="$emit('back')">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>

      <div class="pgl-title-section">
        <h1 class="pgl-title">
          <span class="material-symbols-outlined">play_arrow</span>
          Resuming Game
        </h1>
        <div class="pgl-room-code">
          <span class="pgl-label">Room</span>
          <button class="pgl-code" @click="copyRoomCode" :title="'Click to copy'">
            {{ roomCode }}
            <span class="material-symbols-outlined">content_copy</span>
          </button>
        </div>
      </div>

      <div class="pgl-scores">
        <div class="pgl-score team-a">
          <span class="pgl-team-name">{{ teamAName }}</span>
          <span class="pgl-score-value">{{ teamAScore }}</span>
        </div>
        <span class="pgl-vs">vs</span>
        <div class="pgl-score team-b">
          <span class="pgl-team-name">{{ teamBName }}</span>
          <span class="pgl-score-value">{{ teamBScore }}</span>
        </div>
      </div>
    </header>

    <!-- Game Info Banner -->
    <div class="pgl-info-banner">
      <span class="material-symbols-outlined">info</span>
      <span>Round {{ roundNumber }} • Waiting for players to rejoin</span>
    </div>

    <!-- Main Content - Player Grid -->
    <main class="pgl-main">
      <!-- Team A Section -->
      <section class="pgl-team team-a">
        <h3 class="pgl-team-title">{{ teamAName }}</h3>
        <div class="pgl-team-players">
          <PausedPlayerSlot
            v-for="player in teamAPlayers"
            :key="player.seat_position"
            :player="player"
            :team="'A'"
            :is-creator="isCreator"
            @replace-with-bot="handleReplaceWithBot"
            @send-invite="handleSendInvite"
          />
        </div>
      </section>

      <!-- Logo Divider -->
      <div class="pgl-divider">
        <div class="pgl-divider-line"></div>
        <img src="/images/logosmall.png" alt="Canasta" class="pgl-logo" />
        <div class="pgl-divider-line"></div>
      </div>

      <!-- Team B Section -->
      <section class="pgl-team team-b">
        <h3 class="pgl-team-title">{{ teamBName }}</h3>
        <div class="pgl-team-players">
          <PausedPlayerSlot
            v-for="player in teamBPlayers"
            :key="player.seat_position"
            :player="player"
            :team="'B'"
            :is-creator="isCreator"
            @replace-with-bot="handleReplaceWithBot"
            @send-invite="handleSendInvite"
          />
        </div>
      </section>
    </main>

    <!-- Footer Actions -->
    <footer class="pgl-footer">
      <div class="pgl-ready-status">
        <span class="material-symbols-outlined">{{ allReadyIcon }}</span>
        <span>{{ readyStatusText }}</span>
      </div>
      <button
        v-if="isCreator"
        class="pgl-btn-resume"
        :disabled="!canResume"
        @click="$emit('resume-game')"
      >
        <span class="material-symbols-outlined">play_arrow</span>
        Resume Game
      </button>
    </footer>

    <!-- Copy Toast -->
    <div v-if="showCopyToast" class="pgl-toast">
      Room code copied!
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import PausedPlayerSlot from './PausedPlayerSlot.vue'

interface PausedPlayer {
  seat_position: number
  team: string
  username: string
  display_name: string
  profile_picture: string
  is_bot: boolean
  current_id: string
  original_user_id?: string | null
  replaced_by_bot?: boolean
  status: 'rejoined' | 'waiting' | 'bot'
}

interface Props {
  roomCode: string
  gameId: string
  players: PausedPlayer[]
  roundNumber: number
  teamAName: string
  teamBName: string
  teamAScore: number
  teamBScore: number
  creatorUserId: string
  currentUserId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'resume-game'): void
  (e: 'replace-with-bot', seatPosition: number): void
  (e: 'send-invite', seatPosition: number): void
}>()

const showCopyToast = ref(false)

// Computed properties
const isCreator = computed(() => {
  return props.currentUserId === props.creatorUserId
})

const teamAPlayers = computed(() => {
  return props.players.filter(p => p.team === 'A').sort((a, b) => a.seat_position - b.seat_position)
})

const teamBPlayers = computed(() => {
  return props.players.filter(p => p.team === 'B').sort((a, b) => a.seat_position - b.seat_position)
})

const readyCount = computed(() => {
  return props.players.filter(p => p.status === 'rejoined' || p.status === 'bot').length
})

const canResume = computed(() => {
  // Need all 4 players ready (either rejoined humans or bots)
  return readyCount.value === 4
})

const readyStatusText = computed(() => {
  const ready = readyCount.value
  if (ready === 4) {
    return 'All players ready!'
  }
  const waiting = 4 - ready
  return `Waiting for ${waiting} player${waiting > 1 ? 's' : ''}...`
})

const allReadyIcon = computed(() => {
  return canResume.value ? 'check_circle' : 'hourglass_empty'
})

// Methods
async function copyRoomCode(): Promise<void> {
  try {
    await navigator.clipboard.writeText(props.roomCode)
    showCopyToast.value = true
    setTimeout(() => {
      showCopyToast.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy room code:', err)
  }
}

function handleReplaceWithBot(seatPosition: number): void {
  emit('replace-with-bot', seatPosition)
}

function handleSendInvite(seatPosition: number): void {
  // Copy room code and emit event
  copyRoomCode()
  emit('send-invite', seatPosition)
}
</script>

<style scoped>
.paused-game-lobby {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0d1f2d 0%, #1a2f3f 50%, #0d1f2d 100%);
  color: var(--text-primary, #fff);
}

/* Header */
.pgl-header {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px 32px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.pgl-back-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--text-primary, #fff);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.pgl-back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(-2px);
}

.pgl-title-section {
  flex: 1;
}

.pgl-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--accent-primary, #c9a227);
  margin: 0 0 8px 0;
}

.pgl-title .material-symbols-outlined {
  font-size: 28px;
}

.pgl-room-code {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pgl-label {
  font-size: 0.875rem;
  color: var(--text-muted, #8899a6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.pgl-code {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(201, 162, 39, 0.15);
  border: 1px solid rgba(201, 162, 39, 0.3);
  border-radius: 8px;
  color: var(--accent-primary, #c9a227);
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pgl-code:hover {
  background: rgba(201, 162, 39, 0.25);
}

.pgl-code .material-symbols-outlined {
  font-size: 18px;
  opacity: 0.7;
}

/* Scores */
.pgl-scores {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pgl-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 20px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
}

.pgl-score.team-a {
  border-left: 3px solid var(--team-1-color, #c9a227);
}

.pgl-score.team-b {
  border-left: 3px solid var(--team-2-color, #c94a4a);
}

.pgl-team-name {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted, #8899a6);
}

.pgl-score-value {
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
}

.pgl-vs {
  font-size: 0.875rem;
  color: var(--text-muted, #8899a6);
  font-weight: 600;
}

/* Info Banner */
.pgl-info-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(90deg, rgba(251, 191, 36, 0.1) 0%, rgba(251, 191, 36, 0.2) 50%, rgba(251, 191, 36, 0.1) 100%);
  border-bottom: 1px solid rgba(251, 191, 36, 0.2);
  color: #fbbf24;
  font-weight: 500;
}

.pgl-info-banner .material-symbols-outlined {
  font-size: 20px;
}

/* Main Content */
.pgl-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  gap: 32px;
}

/* Team Sections */
.pgl-team {
  width: 100%;
  max-width: 800px;
}

.pgl-team-title {
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 16px 0;
  padding-left: 8px;
}

.team-a .pgl-team-title {
  color: var(--team-1-color, #c9a227);
  border-left: 3px solid var(--team-1-color, #c9a227);
}

.team-b .pgl-team-title {
  color: var(--team-2-color, #c94a4a);
  border-left: 3px solid var(--team-2-color, #c94a4a);
}

.pgl-team-players {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

/* Divider */
.pgl-divider {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 800px;
}

.pgl-divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
}

.pgl-logo {
  width: 48px;
  height: 48px;
  opacity: 0.6;
}

/* Footer */
.pgl-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.pgl-ready-status {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-muted, #8899a6);
}

.pgl-ready-status .material-symbols-outlined {
  font-size: 24px;
  color: #fbbf24;
}

.pgl-btn-resume {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
}

.pgl-btn-resume:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4);
}

.pgl-btn-resume:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  box-shadow: none;
}

.pgl-btn-resume .material-symbols-outlined {
  font-size: 24px;
}

/* Toast */
.pgl-toast {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  background: rgba(34, 197, 94, 0.9);
  border-radius: 8px;
  color: white;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  animation: fadeInOut 2s ease;
}

@keyframes fadeInOut {
  0%, 100% { opacity: 0; transform: translateX(-50%) translateY(10px); }
  10%, 90% { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* Material icons */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Responsive */
@media (max-width: 768px) {
  .pgl-header {
    flex-wrap: wrap;
    padding: 16px 20px;
    gap: 16px;
  }

  .pgl-scores {
    width: 100%;
    justify-content: center;
  }

  .pgl-main {
    padding: 24px 16px;
  }

  .pgl-team-players {
    grid-template-columns: 1fr;
  }

  .pgl-footer {
    flex-direction: column;
    gap: 16px;
    padding: 16px 20px;
  }

  .pgl-btn-resume {
    width: 100%;
    justify-content: center;
  }
}
</style>
